// ============================================================
// API 响应适配器：把 globalshop API 的 snake_case 字段映射为 camelCase
// 同时把分页结构 {data:{items, total, page, pageSize}} 展平为
// 前端期望的 {data:[...], pagination:{total, totalPages}}
// ============================================================

const PRODUCT_KEYS = [
  'category_id', 'brand_id', 'brand_category_id', 'sort_order',
  'is_active', 'is_featured', 'is_new', 'sales_count', 'review_count',
  'main_image', 'original_price', 'short_description',
  'category_name', 'brand_name', 'stock_alert',
  'source_1688_url', 'source_1688_id', 'source_1688_seller',
  'source_1688_price_cny', 'markup_price', 'markup_currency',
  'meta_title', 'meta_description', 'activity_tags', 'sku_options',
  'product_images', 'product_variants', 'images_list',
  'created_at', 'updated_at'
]

const CATEGORY_KEYS = [
  'sort_order', 'is_active', 'parent_id', 'show_on_home',
  'created_at', 'updated_at'
]

const BRAND_KEYS = [
  'is_featured', 'is_active', 'sort_order',
  'created_at', 'updated_at'
]

const ORDER_KEYS = [
  'order_number', 'user_id', 'user_email', 'user_name',
  'payment_status', 'payment_method', 'payment_method',
  'shipping_fee', 'shipping_address', 'tracking_number', 'tracking_no',
  'internal_note', 'admin_note', 'note',
  'shipped_at', 'completed_at', 'cancelled_at',
  'created_at', 'updated_at', 'last_login_at'
]

const USER_KEYS = [
  'member_level', 'last_login_at', 'email_verified',
  'preferred_locale', 'country_code', 'created_at', 'updated_at'
]

function snakeToCamel(s) {
  return s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())
}

function aliasItem(obj, keys) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  for (const k of keys) {
    if (obj[k] !== undefined && obj[snakeToCamel(k)] === undefined) {
      obj[snakeToCamel(k)] = obj[k]
    }
  }
  // 一些订单字段的兼容性
  if (obj.order_no !== undefined && obj.order_number === undefined) obj.order_number = obj.order_no
  if (obj.tracking_no !== undefined && obj.tracking_number === undefined) obj.tracking_number = obj.tracking_no
  if (obj.internal_note === undefined && obj.admin_note !== undefined) obj.internal_note = obj.admin_note
  if (obj.internal_note === undefined && obj.note !== undefined && obj.status) obj.internal_note = obj.note
  return obj
}

function aliasArray(arr, keys) {
  if (!Array.isArray(arr)) return arr
  for (const item of arr) aliasItem(item, keys)
  return arr
}

function detectKind(url) {
  if (!url) return null
  if (/\/api\/products(\/|\?|$)/.test(url)) return 'product'
  if (/\/api\/categories(\/|\?|$)/.test(url)) return 'category'
  if (/\/api\/brands(\/|\?|$)/.test(url)) return 'brand'
  if (/\/api\/orders(\/|\?|$)/.test(url)) return 'order'
  if (/\/api\/admin\/orders(\/|\?|$)/.test(url)) return 'order'
  if (/\/api\/users(\/|\?|$)/.test(url)) return 'user'
  if (/\/api\/admin\/users(\/|\?|$)/.test(url)) return 'user'
  if (/\/api\/coupons(\/|\?|$)/.test(url)) return 'coupon'
  // 客服端点
  if (/\/api\/admin\/chat(\/|\?|$)/.test(url)) return 'chat'
  if (/\/api\/chat(\/|\?|$)/.test(url)) return 'chat'
  // 用户本人相关端点（与 /api/users 同样需要 snake→camel 转换）
  if (/\/api\/me(\/|\?|$)/.test(url)) return 'user'
  if (/\/api\/auth\/profile(\/|\?|$)/.test(url)) return 'user'
  if (/\/api\/auth\/change-password(\/|\?|$)/.test(url)) return 'user'
  return null
}

const KEY_MAP = {
  product: PRODUCT_KEYS,
  category: CATEGORY_KEYS,
  brand: BRAND_KEYS,
  order: ORDER_KEYS,
  user: USER_KEYS,
  coupon: ['min_order', 'max_discount', 'usage_limit', 'per_user_limit', 'starts_at', 'expires_at', 'is_active', 'created_at', 'updated_at'],
  // 客服消息字段
  chat: [
    'sender_role', 'sender_id', 'is_read', 'read_at',
    'product_id', 'order_id', 'attachment_url',
    'created_at', 'updated_at'
  ]
}

function adaptPagination(data) {
  // 把 {total, page, pageSize, items:[...]} 展平为 [...]
  // 并在 payload._meta 中保留分页信息
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data
  if (data.items && Array.isArray(data.items)) {
    // 同时给顶层加一个 items 别名，方便旧代码 data.data.items 仍能工作
    const items = data.items
    const meta = {
      total: data.total,
      page: data.page,
      pageSize: data.pageSize,
      pagination: {
        total: data.total,
        totalPages: data.pageSize ? Math.ceil(data.total / data.pageSize) : 1,
        page: data.page,
        pageSize: data.pageSize
      }
    }
    return { items, meta, _isPaged: true }
  }
  return data
}

export function installApiAdapter(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => {
      try {
        const url = response.config?.url || ''
        const data = response.data
        if (!data || typeof data !== 'object' || !('success' in data)) return response

        const kind = detectKind(url)
        const keys = kind ? KEY_MAP[kind] : null

        // 1) 处理 data 字段
        let payload = data.data
        // 分页字段可能在 data 顶层（后端返回 {success, total, page, pageSize, data:{items}}）
        // 也可能在 payload 里（后端返回 {success, data:{items, total, page, pageSize}}）
        const total = data.total ?? payload?.total
        const page = data.page ?? payload?.page
        const pageSize = data.pageSize ?? payload?.pageSize
        const items = payload?.items || (Array.isArray(payload) ? payload : null)

        if (items && Array.isArray(items)
            && total !== undefined && page !== undefined && pageSize !== undefined) {
          // 分页结构 — 展平 data.data = items
          if (keys) aliasArray(items, keys)
          response.data.data = items
          response.data.pagination = {
            total,
            totalPages: pageSize ? Math.ceil(total / pageSize) : 1,
            page,
            pageSize
          }
          response.data._paged = true
        } else if (Array.isArray(payload)) {
          if (keys) aliasArray(payload, keys)
          response.data.data = payload
        } else if (payload && typeof payload === 'object') {
          // 单个对象（如登录返回的 user）
          if (keys) aliasItem(payload, keys)
          response.data.data = payload
        }
      } catch (e) {
        // 静默失败，绝不阻塞业务
        // console.warn('[apiAdapter] adapt error:', e)
      }
      return response
    },
    (err) => Promise.reject(err)
  )
}
